import { useState, useEffect } from 'react';

const BETA_CODE = 'phlbeta2026';
const STORAGE_KEY = 'phlrenting_beta_access';

export default function BetaGate({ children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setUnlocked(localStorage.getItem(STORAGE_KEY) === BETA_CODE);
    setLoading(false);
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    if (input.trim().toLowerCase() === BETA_CODE) {
      localStorage.setItem(STORAGE_KEY, BETA_CODE);
      setUnlocked(true);
    } else {
      setError('Invalid access code. Check your invite email and try again.');
      setInput('');
    }
  }

  if (loading) return null;
  if (unlocked) return children;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.wordmark}>
            <div style={styles.icon}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2C5.24 2 3 4.24 3 7c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5zm0 6.5A1.5 1.5 0 1 1 8 5a1.5 1.5 0 0 1 0 3z" fill="#fff"/>
              </svg>
            </div>
            <span style={styles.wordmarkText}>PHL<span style={styles.wordmarkAccent}>Renting</span></span>
          </div>
          <div style={styles.badge}>Private Beta</div>
          <p style={styles.subtitle}>Enter your beta access code to continue</p>
        </div>

        <form style={styles.body} onSubmit={handleSubmit}>
          <label style={styles.label} htmlFor="beta-code">Access code</label>
          <input
            id="beta-code"
            style={styles.input}
            type="text"
            value={input}
            onChange={e => { setInput(e.target.value); setError(''); }}
            placeholder="Enter code from your invite email"
            autoComplete="off"
            autoFocus
          />
          {error && <p style={styles.error} role="alert">{error}</p>}
          <button style={styles.button} type="submit">Enter</button>
          <p style={styles.helper}>
            Don't have a code?{' '}
            <a href="/" style={styles.link}>Request beta access</a>
          </p>
        </form>
      </div>

      <footer style={styles.footer}>
        Confidential &mdash; Beta Preview Only &mdash; &copy; phlrenting.com 2026
      </footer>
    </div>
  );
}

const styles = {
  overlay: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #1e3a5f 0%, #152a45 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1.25rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
    width: '100%',
    maxWidth: '420px',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%)',
    padding: '2rem 2rem 1.75rem',
    textAlign: 'center',
  },
  wordmark: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    marginBottom: '0.75rem',
  },
  icon: {
    width: '28px',
    height: '28px',
    background: '#4a9eff',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  wordmarkText: {
    fontSize: '1.3rem',
    fontWeight: '800',
    color: '#ffffff',
    letterSpacing: '-0.02em',
  },
  wordmarkAccent: {
    color: '#7bb8ff',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(74,158,255,0.2)',
    border: '1px solid rgba(74,158,255,0.4)',
    color: '#7bb8ff',
    fontSize: '0.7rem',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '0.25rem 0.65rem',
    borderRadius: '999px',
    marginBottom: '0.85rem',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.72)',
    margin: 0,
  },
  body: {
    padding: '1.75rem 2rem 2rem',
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '0.45rem',
    letterSpacing: '0.01em',
  },
  input: {
    height: '46px',
    padding: '0 0.9rem',
    border: '1.5px solid #e5e7eb',
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
    color: '#1f2937',
    background: '#f8fafc',
    outline: 'none',
  },
  error: {
    fontSize: '0.78rem',
    color: '#ef4444',
    marginTop: '0.4rem',
    marginBottom: '0',
  },
  button: {
    marginTop: '1rem',
    height: '46px',
    background: '#1e3a5f',
    color: '#ffffff',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  helper: {
    fontSize: '0.78rem',
    color: '#9ca3af',
    marginTop: '1rem',
    textAlign: 'center',
  },
  link: {
    color: '#4a9eff',
    textDecoration: 'none',
  },
  footer: {
    marginTop: '1.5rem',
    fontSize: '0.72rem',
    color: 'rgba(255,255,255,0.35)',
    letterSpacing: '0.02em',
    textAlign: 'center',
  },
};
